import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Select } from "antd";
import { debounce } from "lodash";
import { useNotification } from "../../NotificationContext";
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderById,
} from "../../apis/purchaseorder";
import { PurchaseOrder } from "../../interfaces/PurchaseOrder";
import { getPharmacyItems } from "../../apis/pharmacyItems";
import { getSuppliers } from "../../apis/Supplier";

const { Option } = Select;

type OrderItem = {
  id: number;
  name: string;
  quantity: number;
  purchasePrice: number;
  sellingPrice: number;
  tax: string;
};

type Errors = {
  supplier?: string;
  item?: string;
  date?: string;
  amount?: string;
};

const AddPurchaseOrder = () => {
  const { id } = useParams<{ id?: string }>();
  const editId = id ? Number(id) : undefined;
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [suppliers, setSuppliers] = useState<{ id: number; name: string }[]>(
    []
  );
  const [selectedSupplier, setSelectedSupplier] = useState<number | undefined>(
    undefined
  );
  const [selectedItems, setSelectedItems] = useState<OrderItem[]>([]);
  // const [orderDate, setOrderDate] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  // const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string>("");
  const [status, setStatus] = useState<string>("draft");
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState<boolean>(false);
  const [itemSelectValue, setItemSelectValue] = useState<number | any>(
    undefined
  );

  const today = new Date().toISOString().slice(0, 10); 
  const [orderDate, setOrderDate] = useState<string>(today);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(today);

  const fetchSuppliers = debounce((search: string = "") => {
    const params: any = {};
    if (search.trim()) params.search = search.trim();
    getSuppliers(params)
      .then((res: any) => setSuppliers(res.data || []))
      .catch((err) => console.error(err));
  }, 400);

  useEffect(() => {
    fetchSuppliers("");
    fetchPharmacyItems("");

    if (!editId) return;

    getPurchaseOrderById(editId)
      .then((res: any) => {
        const order = res.data;

        setSelectedSupplier(order.supplierId);

        setOrderDate((order.orderDate || order.order_date)?.slice(0, 10) || "");

        setExpectedDeliveryDate(
          (order.expectedDeliveryDate || order.expected_delivery_date)?.slice(
            0,
            10
          ) || ""
        );

        setStatus(order.status);

        // Items mapping
        setSelectedItems(
          order.items.map((item: any) => ({
            id: item.pharmacyItemId,
            name: item.pharmacyItem?.name || "",
            quantity: Number(item.quantityOrdered),
            purchasePrice: Number(item.unitPrice),
            sellingPrice: Number(item.sellingPrice || 0),
            tax: item.taxPercentage?.toString() || "0",
          }))
        );
      })
      .catch((err) => console.error(err));
  }, [editId]);

  // const fetchPharmacyItems = async (search: string = "") => {
  //   setIsLoadingItems(true);
  //   try {
  //     const params: any = { autocomplete: true };
  //     if (search && search.trim()) {
  //       // Send search term for both name and barcode - backend will search in both fields
  //       params.name = search.trim();
  //       params.barcode = search.trim();
  //     }
  //     // const items = await getPharmacyItems(params);
  //     // console.log("Pharmacy Items Response:", items);
  //     // setPharmacyItems(Array.isArray(items) ? items : []);
  //     const res: any = await getPharmacyItems(params);
  //     setPharmacyItems(Array.isArray(res?.data) ? res.data : []);

  //   } catch (err) {
  //     console.error(err);
  //     setPharmacyItems([]);
  //   } finally {
  //     setIsLoadingItems(false);
  //   }
  // };

  const fetchPharmacyItems = async (search: string = "") => {
  setIsLoadingItems(true);
  try {
    const params: any = {
      autocomplete: true,
    };

    if (search.trim()) {
      params.search = search.trim(); //  name OR barcode
    }

    const res: any = await getPharmacyItems(params);
    setPharmacyItems(Array.isArray(res?.data) ? res.data : []);
  } catch (e) {
    setPharmacyItems([]);
  } finally {
    setIsLoadingItems(false);
  }
};

//   const debouncedFetchItemsRef = useRef(
//     debounce((query: string) => {
//       setIsLoadingItems(true);
//       const params: any = { autocomplete: true };
//       if (query && query.trim()) {
//         // Send search term for both name and barcode - backend will search in both fields
//         params.search = query.trim();
//       }
//       getPharmacyItems(params)
//         // .then((items) => {
//         //   setPharmacyItems(Array.isArray(items) ? items : []);
//         // })
//         .then((res: any) => {
//          setPharmacyItems(Array.isArray(res?.data) ? res.data : []);
//          })

//         .catch((err) => {
//           console.error(err);
//           setPharmacyItems([]);
//         })
//         .finally(() => {
//           setIsLoadingItems(false);
//         });
//     }, 500)
//   );

const debouncedFetchItemsRef = useRef(
  debounce((query: string) => {
    fetchPharmacyItems(query);
  }, 400)
);

  const handleItemSearch = (value: string) => {
  debouncedFetchItemsRef.current.cancel();

  if (value && value.trim()) {
    debouncedFetchItemsRef.current(value);
  } else {
    fetchPharmacyItems("");
  }
};

  const handleSupplierSearch = (value: string) => {
    fetchSuppliers(value);
  };

  const handleAddItem = (itemId: number) => {
    const item = pharmacyItems.find((i) => i.id === itemId);
    if (item && !selectedItems.some((i) => i.id === item.id)) {
      setSelectedItems([
        ...selectedItems,
        {
          id: item.id,
          name: item.name,
          quantity: 1,
          purchasePrice: 0,
          sellingPrice: Number(item.sellingPrice || 0),
          tax: "0",
        },
      ]);
      // Clear the select value after adding
      setItemSelectValue(undefined);
      // Refresh items list to show all items again
      fetchPharmacyItems();
    }
  };

  const handleSellingPriceChange = (id: number, value: number) => {
  setSelectedItems(
    selectedItems.map((i) =>
      i.id === id ? { ...i, sellingPrice: value || 0 } : i
    )
  );
};

  const handleRemoveItem = (id: number) => {
    setSelectedItems(selectedItems.filter((i) => i.id !== id));
  };

  const handleQuantityChange = (id: number, value: number) => {
    setSelectedItems(
      selectedItems.map((i) =>
        i.id === id ? { ...i, quantity: value || 0 } : i
      )
    );
  };

  const handlePriceChange = (id: number, value: number) => {
    setSelectedItems(
      selectedItems.map((i) =>
        i.id === id ? { ...i, purchasePrice: value || 0 } : i
      )
    );
  };

  const handleTaxChange = (id: number, value: string) => {
    setSelectedItems(
      selectedItems.map((i) => (i.id === id ? { ...i, tax: value } : i))
    );
  };

  const calculateTotal = () => {
    return selectedItems.reduce((total, item) => {
      const subtotal = item.quantity * item.purchasePrice;
      const taxAmount = (subtotal * Number(item.tax)) / 100;
      return total + subtotal + taxAmount;
    }, 0);
  };

  const validateForm = () => {
    const e: Errors = {};
    if (!selectedSupplier) e.supplier = "* Supplier is required";
    if (selectedItems.length === 0) e.item = "* At least one item is required";
    if (!orderDate) e.date = "* Order Date is required";
    return { hasErrors: Object.keys(e).length > 0, errors: e };
  };

  const handleSubmit = async () => {
    const { hasErrors, errors } = validateForm();
    if (hasErrors) return setErrors(errors);

    const payload: PurchaseOrder = {
      poNumber: "PO" + new Date().getTime(),
      supplierId: selectedSupplier!,
      clinicId: 1,
      orderDate,
      expectedDeliveryDate: expectedDeliveryDate || null,
      status: status as PurchaseOrder["status"],
      totalAmount: calculateTotal(),
      items: selectedItems.map((item) => ({
        pharmacy_item_id: item.id,
        quantity_ordered: item.quantity,
        unit_price: item.purchasePrice,
        selling_price: item.sellingPrice,
        tax_percentage: Number(item.tax),
      })),
    };

    try {
      if (editId) {
        // UPDATE
        await updatePurchaseOrder(editId, payload);
        showNotification(
          "success",
          "Purchase Order Updated Successfully",
          "",
          "topRight"
        );
      } else {
        // CREATE
        await createPurchaseOrder(payload);
        showNotification(
          "success",
          "Purchase order saved successfully",
          "",
          "topRight"
        );
      }

      navigate(-1);
    } catch (err) {
      console.error(err);
      showNotification("error", "Something went wrong", "", "topRight");
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-lg font-semibold text-gray-900">
        {editId ? "Update Purchase Order" : "Add Purchase Order"}
      </h1>
      <p className="mt-1 text-sm text-gray-600">
        {editId
          ? "Edit the form to update the purchase order."
          : "Fill the form to create a new purchase order."}
      </p>

      <div className="p-4 mt-4 bg-white rounded-lg shadow-sm border border-gray-100">
        {/* Basic Information Section */}
        <div className="grid grid-cols-1 gap-3 mb-5 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Select Supplier <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <Select
                style={{ width: "100%" }}
                value={selectedSupplier}
                onChange={(value) => {
                  setSelectedSupplier(value);
                  setErrors({ ...errors, supplier: undefined });
                }}
                showSearch
                allowClear
                onSearch={handleSupplierSearch}
                filterOption={false}
                placeholder="Select Supplier"
                className="w-full"
                size="middle"
              >
                {suppliers.map((sup) => (
                  <Option key={sup.id} value={sup.id}>
                    {sup.name}
                  </Option>
                ))}
              </Select>
            </div>
            {errors.supplier && (
              <span className="text-sm text-red-500">{errors.supplier}</span>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Order Date <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <input
                type="date"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={orderDate}
                onChange={(e) => {
                  setOrderDate(e.target.value);
                  setErrors({ ...errors, date: undefined });
                }}
              />
            </div>
            {errors.date && (
              <span className="text-sm text-red-500">{errors.date}</span>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Expected Delivery Date
            </label>
            <div className="mt-1">
              <input
                type="date"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Status
            </label>
            <div className="mt-1">
              <Select
                style={{ width: "100%" }}
                value={status}
                onChange={setStatus}
                placeholder="Select Status"
                className="w-full"
                size="middle"
              >
                <Option value="draft">Draft</Option>
                <Option value="pending">Pending</Option>
                <Option value="paid">Paid</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="pt-6 border-t border-gray-200">
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium leading-6 text-gray-900">
              Select Items <span className="text-red-500">*</span>
            </label>
            <Select
              showSearch
              allowClear
              style={{ width: "50%" }}
              placeholder="Search by name"
              value={itemSelectValue}
              onChange={(val) => {
                if (val) handleAddItem(val);
                setItemSelectValue(null);
              }}
              // onChange={handleAddItem}
              onSearch={handleItemSearch}
              filterOption={false}
              loading={isLoadingItems}
              size="large"
              className="w-full"
              notFoundContent={isLoadingItems ? "Loading..." : "No items found"}
              optionLabelProp="label"
            >
              {/* {pharmacyItems
                .filter(
                  (item) =>
                    !selectedItems.some((selected) => selected.id === item.id)
                )
                .map((item) => (
                  <Option key={item.id} value={item.id} label={item.name}>
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="flex gap-2 text-xs text-gray-500">
                        {item.form && <span>{item.form}</span>}
                        {item.barcode && (
                          <span className="text-gray-400">
                            Barcode: {item.barcode}
                          </span>
                        )}
                      </div>
                    </div>
                  </Option>
                ))} */}
              {pharmacyItems.map((item) => {
                const isSelected = selectedItems.some(
                  (selected) => selected.id === item.id
                );

                return (
                  <Option
                    key={item.id}
                    value={item.id}
                    label={item.name}
                    // disabled={isSelected}
                  >
                    <div>
                      <div className="font-medium">
                        {item.name}
                      </div>
                      <div className="flex gap-2 text-xs text-gray-500">
                        {item.form && <span>{item.form}</span>}
                        {item.barcode && (
                          <span className="text-gray-400">
                            Barcode: {item.barcode}
                          </span>
                        )}
                      </div>
                    </div>
                  </Option>
                );
              })}
            </Select>
            {errors.item && (
              <span className="block mt-1 text-sm text-red-500">
                {errors.item}
              </span>
            )}
          </div>

          {selectedItems.length > 0 && (
            <div className="mt-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Item
                      </th>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Purchase Price 
                      </th>
                      {/* <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Selling Price / pc
                      </th> */}
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Tax %
                      </th>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Subtotal
                      </th>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedItems.map((item) => {
                      const subtotal = item.quantity * item.purchasePrice;
                      const taxAmount = (subtotal * Number(item.tax)) / 100;
                      const total = subtotal + taxAmount;

                      return (
                        <tr key={item.id}>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {item.name}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="1"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={item.quantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  item.id,
                                  Number(e.target.value)
                                )
                              }
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={item.purchasePrice}
                              onChange={(e) =>
                                handlePriceChange(
                                  item.id,
                                  Number(e.target.value)
                                )
                              }
                            />
                          </td>
                          {/* <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              value={item.sellingPrice || 0} // add sellingPrice in your state
                              onChange={(e) =>
                                handleSellingPriceChange(item.id, Number(e.target.value))
                              }
                            />
                          </td> */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Select
                              style={{ width: "100%" }}
                              value={item.tax}
                              onChange={(val) =>
                                handleTaxChange(item.id, String(val))
                              }
                              size="small"
                            >
                              <Option value="0">0%</Option>
                              <Option value="5">5%</Option>
                              <Option value="12">12%</Option>
                              <Option value="18">18%</Option>
                              <Option value="28">28%</Option>
                            </Select>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                            ₹{subtotal.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                            ₹{total.toFixed(2)}
                          </td>
                          <td className="px-4 py-4 text-sm whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(item.id)}
                              className="font-medium text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-4 text-sm font-semibold text-right text-gray-900"
                      >
                        Grand Total:
                      </td>
                      <td
                        colSpan={3}
                        className="px-4 py-4 text-sm font-bold text-gray-900"
                      >
                        ₹{calculateTotal().toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-x-6 justify-start items-center py-4 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleSubmit}
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
          >
            {editId ? "Update" : "Save"}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPurchaseOrder;
